// pages/CI/LiveActivity.js
//
// Wristband activity recognition — "Activity Intelligence".
//   Tab 1 "Live"       : each worker's latest recognised activity + confidence,
//                        refreshed every 5s (paused while the browser tab is hidden).
//   Tab 2 "Time Breakdown": time-per-activity for one worker on one PKT calendar
//                        day (HOD reporting), with an "Analyse this day" button
//                        that replays the worker's stored band readings through
//                        the model when that day hasn't been analysed yet.
//
// Backend: /api/activity-predictions/* (sowash-backend/routes/activityPredictionRoutes.js).
// Main-system auth only (localStorage.token via getAuthHeader) — nothing Mideast.
//
// ⚠ "Live" is only as live as the band's uploads. Today the FO app uploads a
// whole recording AFTER the fact (tracker_data, one batch ~a minute after
// capture), so unless a worker's data is being uploaded continuously the Live
// tab shows their LAST analysed activity, labelled "Last known" with its age —
// it never presents a stale activity as current. See services/activityPredictor.js.
//
// ⚠ Time zones: the API returns real instants (UTC ISO). Everything is shown in
// Asia/Karachi explicitly, and a selected day is turned into a PKT-midnight to
// PKT-midnight instant range before it is sent — never a bare date — so the
// VPS's own zone (Europe/Berlin, with DST) can't move a day boundary.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeader } from 'helpers/auth';
import {
  Activity, RefreshCw, Radio, Clock, BarChart3, Play, WifiOff, User, CheckCircle2, AlertTriangle,
} from 'lucide-react';

// ── Design tokens (same set the other CI data pages use) ─────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  surf2:   'var(--nd-surface-2)',
  border:  'var(--nd-w08)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx35)',
  blue:    '#3B82F6',
  teal:    '#22D3A5',
  amber:   '#FBBF24',
  red:     '#F87171',
  green:   '#34D399',
  font:    "'DM Sans', sans-serif",
  mono:    "'DM Mono', monospace",
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  @keyframes la-spin   { to { transform: rotate(360deg); } }
  @keyframes la-fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes la-pulse  { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(1.5)} }

  .la-card { animation: la-fadeUp 0.35s ease both; transition: transform 0.2s, box-shadow 0.2s; }
  .la-card.la-click { cursor: pointer; }
  .la-card.la-click:hover { transform: translateY(-3px); box-shadow: 0 14px 32px rgba(0,0,0,0.45) !important; }

  .la-tab {
    flex: 1; padding: 10px 20px; border: none; border-radius: 9px;
    font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 700;
    cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: all 0.18s;
  }
  .la-tab-inactive { background: transparent; color: var(--nd-tx30); }
  .la-tab-inactive:hover { background: var(--nd-w05); color: var(--nd-tx55); }
  .la-tab-active   { background: linear-gradient(135deg,#3B82F6,#22D3A5); color: var(--nd-text-strong); box-shadow: 0 4px 14px rgba(59,130,246,0.30); }

  .la-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 8px 16px; border: 1px solid var(--nd-w12); border-radius: 9px;
    background: var(--nd-w05); color: var(--nd-tx70);
    font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; transition: all 0.18s;
  }
  .la-btn:hover:not(:disabled) { background: var(--nd-w10); color: var(--nd-text); }
  .la-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .la-input {
    background: var(--nd-w05); border: 1px solid var(--nd-w12); border-radius: 9px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px;
    padding: 8px 12px; outline: none; color-scheme: dark;
  }
  .la-input:focus { border-color: #3B82F6; }

  .la-th {
    padding: 11px 14px; text-align: left;
    font-size: 9px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--nd-tx30); border-bottom: 1px solid var(--nd-w08); white-space: nowrap;
  }
  .la-tr { border-bottom: 1px solid var(--nd-w05); }
  .la-tr:last-child { border-bottom: none; }
  .la-td { padding: 13px 14px; vertical-align: middle; }
`;

// ── Constants & helpers ──────────────────────────────────────────────
const BASE = process.env.REACT_APP_API_BASE_URL;
const API = `${BASE}/activity-predictions`;
const PKT = 'Asia/Karachi';
const POLL_MS = 5000;
const LIVE_WITHIN_SECONDS = 15;   // newer than this = "Live", otherwise "Last known"
const RECENT_WITHIN_SECONDS = 10 * 60; // older than this = "Inactive": kept out of the card grid, listed collapsed

const ACTIVITY_CFG = {
  mopping:  { color: '#60A5FA', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.28)'  },
  brushing: { color: '#F472B6', bg: 'rgba(244,114,182,0.12)', border: 'rgba(244,114,182,0.28)' },
  screwing: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.28)'  },
  watering: { color: '#22D3EE', bg: 'rgba(34,211,238,0.12)',  border: 'rgba(34,211,238,0.28)'  },
  idle:     { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.28)' },
  unknown:  { color: '#A78BFA', bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.28)' },
};
const activityCfg = (a) => ACTIVITY_CFG[(a || '').toLowerCase()] || ACTIVITY_CFG.unknown;

/** Today's calendar date in PKT as YYYY-MM-DD (en-CA formats that way). */
const todayPkt = () => new Date().toLocaleDateString('en-CA', { timeZone: PKT });

/** A PKT calendar day -> the [from, to) instants as UTC ISO strings. */
const pktDayRange = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  return {
    from: new Date(`${dateStr}T00:00:00+05:00`).toISOString(),
    to: new Date(`${next}T00:00:00+05:00`).toISOString(),
  };
};

const fmtTimePkt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleTimeString('en-GB', { timeZone: PKT, hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtDateTimePkt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleString('en-GB', {
    timeZone: PKT, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const fmtDuration = (secs) => {
  const s = Math.round(secs || 0);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${r}s`;
};

const fmtAge = (secs) => {
  const s = Math.max(0, Math.round(secs));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function Avatar({ name, size = 40 }) {
  const gradients = [['#667eea', '#764ba2'], ['#f093fb', '#f5576c'], ['#4facfe', '#00f2fe'], ['#43e97b', '#38f9d7'], ['#fa709a', '#fee140']];
  const hash = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const [c1, c2] = gradients[hash % gradients.length];
  const initials = (name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size, minWidth: size, borderRadius: 10,
      background: `linear-gradient(135deg, ${c1}, ${c2})`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--nd-text-strong)', fontWeight: 700, fontSize: size * 0.35, boxShadow: '0 3px 10px rgba(0,0,0,0.30)',
    }}>{initials}</div>
  );
}

function ActivityChip({ activity }) {
  const c = activityCfg(activity);
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`, padding: '4px 12px', borderRadius: 6,
      fontSize: 11, fontWeight: 700, textTransform: 'capitalize', letterSpacing: '0.06em', fontFamily: T.mono,
    }}>{activity}</span>
  );
}

const Panel = ({ children, style }) => (
  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', ...style }}>{children}</div>
);

const Notice = ({ color = T.amber, Icon = AlertTriangle, children }) => (
  <div style={{
    display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 16px', borderRadius: 12, marginBottom: 14,
    background: `${color}12`, border: `1px solid ${color}38`, color: T.text, fontSize: 12, lineHeight: 1.5,
  }}>
    <Icon size={15} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
    <div>{children}</div>
  </div>
);

// ══════════════════════════════════════════════════════════════════
//   MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
const LiveActivity = () => {
  const [tab, setTab] = useState('live');

  // ── Live tab ──
  const [live, setLive] = useState({ workers: [], fetchedAt: 0 });
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveError, setLiveError] = useState(false);
  const [serviceOnline, setServiceOnline] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [showInactive, setShowInactive] = useState(false);
  const liveInFlight = useRef(false);

  // ── Breakdown tab ──
  const [workers, setWorkers] = useState([]);
  const [userId, setUserId] = useState('');
  const [date, setDate] = useState(todayPkt());
  const [summary, setSummary] = useState(null);
  const [sumLoading, setSumLoading] = useState(false);
  const [sumError, setSumError] = useState('');
  const [analysis, setAnalysis] = useState(null);   // { state, windows, error } while/after an analysis
  const sumReq = useRef(0);

  // ── Live polling ──
  const fetchLive = useCallback(async () => {
    if (liveInFlight.current || document.hidden) return;
    liveInFlight.current = true;
    try {
      const config = getAuthHeader();
      const [latestRes, healthRes] = await Promise.allSettled([
        axios.get(`${API}/latest`, config),
        axios.get(`${API}/health`, config),
      ]);
      if (latestRes.status === 'fulfilled') {
        const list = latestRes.value.data?.workers;
        setLive({ workers: Array.isArray(list) ? list : [], fetchedAt: Date.now() });
        setLiveError(false);
      } else {
        setLiveError(true);          // keep the last good data on screen
      }
      if (healthRes.status === 'fulfilled') setServiceOnline(!!healthRes.value.data?.online);
    } finally {
      liveInFlight.current = false;
      setLiveLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== 'live') return undefined;
    fetchLive();
    const poll = setInterval(fetchLive, POLL_MS);
    const tick = setInterval(() => setNow(Date.now()), 1000);   // keeps "Xs ago" moving between polls
    const onVisible = () => { if (!document.hidden) fetchLive(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [tab, fetchLive]);

  // ── Workers for the picker ──
  useEffect(() => {
    axios.get(`${API}/workers`, getAuthHeader())
      .then((r) => setWorkers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setWorkers([]));
  }, []);

  // ── Summary for the selected worker + day ──
  const loadSummary = useCallback(async () => {
    if (!userId || !date) { setSummary(null); return; }
    const reqId = ++sumReq.current;
    setSumLoading(true);
    setSumError('');
    try {
      const { from, to } = pktDayRange(date);
      const r = await axios.get(`${API}/summary`, { ...getAuthHeader(), params: { user_id: userId, from, to } });
      if (reqId === sumReq.current) setSummary(r.data);
    } catch (e) {
      if (reqId === sumReq.current) {
        setSummary(null);
        setSumError(e?.response?.data?.message || 'Could not load the activity summary.');
      }
    } finally {
      if (reqId === sumReq.current) setSumLoading(false);
    }
  }, [userId, date]);

  useEffect(() => { setAnalysis(null); loadSummary(); }, [loadSummary]);

  // ── Analyse this day (background job; poll its status) ──
  const startAnalysis = async () => {
    try {
      const { from, to } = pktDayRange(date);
      const r = await axios.post(`${API}/analyze`, { user_id: Number(userId), from, to }, getAuthHeader());
      setAnalysis({ state: r.data.state, windows: r.data.windows || 0 });
    } catch (e) {
      setAnalysis({ state: 'error', error: e?.response?.data?.message || 'Could not start the analysis.' });
    }
  };

  const analysing = analysis?.state === 'running';
  useEffect(() => {
    if (!analysing) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/analyze/status`, { ...getAuthHeader(), params: { user_id: userId } });
        const job = r.data;
        if (job.state === 'running') {
          setAnalysis({ state: 'running', windows: job.windows || 0 });
        } else if (job.state === 'done') {
          setAnalysis({ state: 'done', windows: job.windows || 0 });
          loadSummary();
        } else {
          setAnalysis({ state: 'error', error: job.error || 'Analysis did not complete.' });
        }
      } catch (e) {
        setAnalysis({ state: 'error', error: 'Lost contact with the server while analysing.' });
      }
    }, 2000);
    return () => clearInterval(id);
  }, [analysing, userId, loadSummary]);

  const openBreakdown = (id) => { setUserId(String(id)); setDate(todayPkt()); setTab('breakdown'); };

  // ── Derived ──
  const liveCards = useMemo(() => {
    const secsSinceFetch = live.fetchedAt ? (now - live.fetchedAt) / 1000 : 0;
    return live.workers
      .map((w) => {
        // A missing age must never read as "just now" — treat it as unknown/very old.
        const age = w.activity_age_seconds == null ? Infinity : w.activity_age_seconds + secsSinceFetch;
        const status = age <= LIVE_WITHIN_SECONDS ? 'live' : age <= RECENT_WITHIN_SECONDS ? 'recent' : 'inactive';
        return { ...w, age, status, isLive: status === 'live' };
      })
      .sort((a, b) => a.age - b.age);
  }, [live, now]);

  // Only workers with activity in the last RECENT_WITHIN_SECONDS get a card. An old
  // result (e.g. "Mopping 70%" from months ago) must not sit in the grid looking
  // like that person is working right now.
  const activeCards = liveCards.filter((w) => w.status !== 'inactive');
  const inactiveCards = liveCards.filter((w) => w.status === 'inactive');
  const liveCount = liveCards.filter((w) => w.isLive).length;
  const recentCount = activeCards.length - liveCount;
  const selectedWorker = workers.find((w) => String(w.user_id) === String(userId));

  // ══════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: T.bg, fontFamily: T.font, color: T.text, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* ── HEADER ── */}
      <Panel style={{ marginBottom: 18 }}>
        <div style={{ padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14 }}>
          <div>
            <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · ACTIVITY INTELLIGENCE</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Worker Activity</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 5 }}>What each worker is doing, recognised from the wristband · times in PKT</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: T.muted }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: serviceOnline === false ? T.red : serviceOnline ? T.green : T.muted,
              animation: serviceOnline ? 'la-pulse 1.6s ease-in-out infinite' : 'none',
            }} />
            {serviceOnline === false ? 'Activity service offline' : serviceOnline ? 'Activity service online' : 'Checking service…'}
          </div>
        </div>
        <div style={{ height: 2, background: `linear-gradient(90deg, ${T.blue}, ${T.teal}, transparent)` }} />
      </Panel>

      {/* ── TABS ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 6, marginBottom: 18, display: 'flex', gap: 6 }}>
        <button className={`la-tab ${tab === 'live' ? 'la-tab-active' : 'la-tab-inactive'}`} onClick={() => setTab('live')}>
          <Radio size={15} /> Live
        </button>
        <button className={`la-tab ${tab === 'breakdown' ? 'la-tab-active' : 'la-tab-inactive'}`} onClick={() => setTab('breakdown')}>
          <BarChart3 size={15} /> Time Breakdown
        </button>
      </div>

      {/* ══ LIVE ══ */}
      {tab === 'live' && (
        <>
          {serviceOnline === false && (
            <Notice color={T.red} Icon={WifiOff}>
              The activity recognition service is not reachable, so no new activity is being recognised. Existing results below are still accurate for the time shown.
            </Notice>
          )}
          {liveError && (
            <Notice>Couldn’t refresh just now — showing the last data received. Retrying every {POLL_MS / 1000}s.</Notice>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 12, color: T.muted }}>
              <span style={{ color: T.green, fontWeight: 700, fontFamily: T.mono }}>{liveCount}</span> live now · {recentCount} recent · {inactiveCards.length} inactive
              {live.fetchedAt > 0 && <> · updated {fmtTimePkt(new Date(live.fetchedAt).toISOString())}</>}
            </div>
            <button className="la-btn" onClick={fetchLive}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {liveLoading ? (
            <Panel style={{ padding: '60px 20px', textAlign: 'center' }}>
              <div style={{ width: 40, height: 40, border: `3px solid ${T.border}`, borderTop: `3px solid ${T.blue}`, borderRadius: '50%', animation: 'la-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <div style={{ color: T.muted, fontSize: 13 }}>Loading activity…</div>
            </Panel>
          ) : liveCards.length === 0 ? (
            <Panel style={{ padding: '60px 20px', textAlign: 'center' }}>
              <Activity size={40} color={T.muted} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No activity recognised yet</div>
              <div style={{ fontSize: 12, color: T.muted, maxWidth: 420, margin: '0 auto' }}>
                Results appear once a worker’s band data has been analysed. Use the Time Breakdown tab to analyse a worker’s day.
              </div>
            </Panel>
          ) : (
            <>
              {liveCount === 0 && (
                <Notice color={T.blue} Icon={Clock}>
                  {activeCards.length === 0 ? (
                    <>No worker has been active in the last {RECENT_WITHIN_SECONDS / 60} minutes. Workers with older results are listed under <b>Inactive</b> below.</>
                  ) : (
                    <>No worker is being tracked live right now. Each card shows the worker’s <b>last known</b> activity and how long ago it was.
                    The band uploads its data after a recording, so live updates need the band to be syncing continuously.</>
                  )}
                </Notice>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
                {activeCards.map((w, i) => {
                  const c = activityCfg(w.activity);
                  const conf = Math.round((w.confidence || 0) * 100);
                  return (
                    <div key={w.user_id} className="la-card la-click" onClick={() => openBreakdown(w.user_id)}
                      style={{
                        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.25)', position: 'relative', overflow: 'hidden',
                        animationDelay: `${Math.min(i, 10) * 0.03}s`, opacity: w.isLive ? 1 : 0.82,
                      }}>
                      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: `linear-gradient(180deg,${c.color},transparent)` }} />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                        <Avatar name={w.name} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name || `Worker #${w.user_id}`}</div>
                          <div style={{ fontSize: 11, color: w.isLive ? T.green : T.muted, display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: w.isLive ? T.green : T.muted, animation: w.isLive ? 'la-pulse 1.6s ease-in-out infinite' : 'none' }} />
                            {w.isLive ? 'Live' : 'Last known'} · {fmtAge(w.age)}
                          </div>
                        </div>
                      </div>
                      <ActivityChip activity={w.activity} />
                      <div style={{ marginTop: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.muted, marginBottom: 5, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                          <span>Confidence</span><span style={{ fontFamily: T.mono, color: T.text }}>{conf}%</span>
                        </div>
                        <div style={{ height: 5, borderRadius: 99, background: 'var(--nd-w10)', overflow: 'hidden' }}>
                          <div style={{ width: `${conf}%`, height: '100%', background: c.color, borderRadius: 99, transition: 'width 0.4s' }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 12, fontFamily: T.mono }}>
                        Activity at {fmtDateTimePkt(w.window_end_at)} PKT
                      </div>
                    </div>
                  );
                })}
              </div>

              {inactiveCards.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <button className="la-btn" onClick={() => setShowInactive((v) => !v)}>
                    {showInactive ? 'Hide' : 'Show'} inactive workers ({inactiveCards.length})
                  </button>
                  {showInactive && (
                    <Panel style={{ marginTop: 10 }}>
                      {inactiveCards.map((w) => (
                        <div key={w.user_id} onClick={() => openBreakdown(w.user_id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer', opacity: 0.7 }}>
                          <Avatar name={w.name} size={30} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.name || `Worker #${w.user_id}`}</div>
                            <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono }}>
                              Inactive · last result {Number.isFinite(w.age) ? fmtAge(w.age) : 'time unknown'} ({w.activity}) · {fmtDateTimePkt(w.window_end_at)} PKT
                            </div>
                          </div>
                        </div>
                      ))}
                    </Panel>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ══ TIME BREAKDOWN ══ */}
      {tab === 'breakdown' && (
        <>
          <Panel style={{ padding: '14px 18px', marginBottom: 14, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <User size={15} color={T.muted} />
            <select className="la-input" value={userId} onChange={(e) => setUserId(e.target.value)} style={{ minWidth: 220 }}>
              <option value="">Select a worker…</option>
              {workers.map((w) => (
                <option key={w.user_id} value={w.user_id}>{w.name || `Worker #${w.user_id}`}</option>
              ))}
            </select>
            <input type="date" className="la-input" value={date} max={todayPkt()} onChange={(e) => e.target.value && setDate(e.target.value)} />
            <div style={{ fontSize: 11, color: T.muted }}>Day runs 00:00–24:00 PKT</div>
          </Panel>

          {!userId ? (
            <Panel style={{ padding: '60px 20px', textAlign: 'center' }}>
              <BarChart3 size={40} color={T.muted} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Pick a worker and a day</div>
              <div style={{ fontSize: 12, color: T.muted }}>You’ll see how that worker’s time was split across activities.</div>
            </Panel>
          ) : sumLoading ? (
            <Panel style={{ padding: '60px 20px', textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</Panel>
          ) : sumError ? (
            <Notice color={T.red}>{sumError}</Notice>
          ) : summary && summary.activities.length > 0 ? (
            <>
              <Panel style={{ padding: '18px 20px', marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Total recognised time</div>
                    <div style={{ fontSize: 32, fontWeight: 900, fontFamily: T.mono, color: T.teal, letterSpacing: '-1px' }}>{fmtDuration(summary.total_seconds)}</div>
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, fontFamily: T.mono, textAlign: 'right' }}>
                    {selectedWorker?.name || `Worker #${userId}`}<br />
                    {fmtTimePkt(summary.first_at)} → {fmtTimePkt(summary.last_at)} PKT
                  </div>
                </div>
                <div style={{ display: 'flex', height: 18, borderRadius: 9, overflow: 'hidden', background: 'var(--nd-w05)' }}>
                  {summary.activities.map((a) => (
                    <div key={a.activity} title={`${a.activity}: ${fmtDuration(a.seconds)} (${a.percent}%)`}
                      style={{ width: `${a.percent}%`, background: activityCfg(a.activity).color, minWidth: a.percent > 0 ? 2 : 0 }} />
                  ))}
                </div>
              </Panel>

              <Panel>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th className="la-th" style={{ paddingLeft: 20 }}>Activity</th>
                        <th className="la-th">Time</th>
                        <th className="la-th">Share</th>
                        <th className="la-th">Avg confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.activities.map((a) => (
                        <tr key={a.activity} className="la-tr">
                          <td className="la-td" style={{ paddingLeft: 20 }}><ActivityChip activity={a.activity} /></td>
                          <td className="la-td" style={{ fontFamily: T.mono, fontWeight: 700 }}>{fmtDuration(a.seconds)}</td>
                          <td className="la-td" style={{ fontFamily: T.mono }}>{a.percent}%</td>
                          <td className="la-td" style={{ fontFamily: T.mono, color: T.muted }}>{Math.round(a.avg_confidence * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>

              <div style={{ marginTop: 12 }}>
                <button className="la-btn" onClick={startAnalysis} disabled={analysing}>
                  <RefreshCw size={14} style={{ animation: analysing ? 'la-spin 0.8s linear infinite' : 'none' }} />
                  {analysing ? `Re-analysing… ${analysis.windows} windows` : 'Re-analyse this day'}
                </button>
              </div>
            </>
          ) : (
            <Panel style={{ padding: '50px 20px', textAlign: 'center' }}>
              <Clock size={40} color={T.muted} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No analysed activity for this day</div>
              <div style={{ fontSize: 12, color: T.muted, maxWidth: 440, margin: '0 auto 16px' }}>
                If this worker wore the band on {date}, analyse the day to turn their sensor data into activity time.
                Days with no band data will stay empty.
              </div>
              <button className="la-btn" onClick={startAnalysis} disabled={analysing} style={{ margin: '0 auto' }}>
                <Play size={14} />
                {analysing ? `Analysing… ${analysis.windows} windows` : 'Analyse this day'}
              </button>
            </Panel>
          )}

          {analysis?.state === 'done' && summary && summary.activities.length === 0 && (
            <div style={{ marginTop: 14 }}>
              <Notice color={T.blue} Icon={CheckCircle2}>
                The analysis finished but found no usable band data for this worker on this day (or fewer than 2.5 seconds of continuous wear).
              </Notice>
            </div>
          )}
          {analysis?.state === 'error' && (
            <div style={{ marginTop: 14 }}><Notice color={T.red}>{analysis.error}</Notice></div>
          )}
        </>
      )}
    </div>
  );
};

export default LiveActivity;
